import { css } from '@emotion/react'
import { Link } from 'gatsby'
import { type ComponentProps } from 'react'

import {
  DatoStructuredText,
  GatsbyImageFocused,
} from '@/features/common'
import { DatoLinkIcon } from '@/features/dato-link'
import { absoluteFill, mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

import { useClassNotesContext } from '../../context/classNotesContext'

type Props = ComponentProps<'section'> & {
  data: Queries.CNNExpandedQuery['datoCmsClassNotesPage']
  allMilestones?: { heading: string; slug: string }[]
  allFacultyStaffNotes?: { heading: string; slug: string }[]
}

export const ClassNotesMilestonesSection = ({
  data,
  allMilestones,
  allFacultyStaffNotes,
  ...props
}: Props) => {
  const { setExpanded } = useClassNotesContext()

  const styles = {
    section: css`
      display: grid;
      grid-template-columns: 1fr 1fr;
      margin: 0 var(--margin) var(--row-54);
      gap: var(--row-24);
      ${mq().ms} {
        margin: 0 1rem var(--row-54);
        grid-template-columns: 1fr;
      }
    `,
    container: css`
      display: grid;
      background-color: ${colors.gray93};
      text-decoration: none;
      color: inherit;
      transition: all 300ms ease;
      --highlight-color: ${colors.red};
      @media (hover: hover) {
        &:hover {
          background-color: ${colors.red};
          color: #fff;
          --highlight-color: #fff;
        }
      }
    `,
    imageWrapper: css`
      align-self: stretch;
      position: relative;
      ${mq().s} {
        height: 60vw;
      }
    `,
    image: css`
      min-height: 100%;
      ${absoluteFill}
    `,
    text: css`
      padding: 2em 3em;
      align-self: center;
      ${mq().ms} {
        padding: 2em;
      }
      ${mq().s} {
        padding: 1.5em;
      }
    `,
    heading: css`
      font-size: var(--fs-48);
      width: 100%;
      border-bottom: 2px solid var(--highlight-color);
      transition: border-color 300ms ease;
      margin: 0 0 0.5em;
      padding-bottom: 0.5em;
    `,
    body: css``,
    links: css`
      display: grid;
      row-gap: 0.5em;
    `,
    link: css`
      display: block;
      max-width: fit-content;
      color: var(--highlight-color);
      transition: color 300ms ease;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      text-decoration: none;
      --font-weight: 500;
      padding: 0.5em 0;
    `,
  }
  return (
    <section
      css={styles.section}
      {...props}
    >
      <Link
        css={styles.container}
        to={`/${allMilestones?.[0].slug}/`}
        onClick={() => {
          setTimeout(() => {
            setExpanded(false)
          }, 10)
        }}
      >
        <div css={styles.text}>
          <h2 css={styles.heading}>{data?.milestonesHeading}</h2>
          <div css={styles.body}>
            <DatoStructuredText data={data?.milestonesBody} />
          </div>
          <div css={styles.link}>
            Explore Milestones
            <DatoLinkIcon iconType={'ARROW'} />
          </div>
        </div>
      </Link>
      <Link
        css={styles.container}
        to={`/${allFacultyStaffNotes?.[0]?.slug}/`}
        onClick={() => {
          setTimeout(() => {
            setExpanded(false)
          }, 10)
        }}
      >
        <div css={styles.text}>
          <h2 css={styles.heading}>{data?.facultyStaffHeading}</h2>
          <div css={styles.body}>
            <DatoStructuredText data={data?.facultyStaffBody} />
          </div>
          <div css={styles.link}>
            Explore Faculty/Staff Notes
            <DatoLinkIcon iconType={'ARROW'} />
          </div>
        </div>
      </Link>
    </section>
  )
}
